/**
 * @fileoverview Helpdesk Table Configuration
 *
 * Centralized configuration for the helpdesk tickets table.
 * Extracts all helpdesk-specific logic from UniversalTable into a reusable config.
 *
 * This configuration includes:
 * - Column definitions (widths, labels, sort keys, visibility)
 * - Picker field mappings (how cells map to picker modals)
 * - Sort logic (how to sort by priority, status, etc.)
 *
 * @module Helpdesk/TableConfig
 */

import {
  TableColumnsConfig,
  PickerConfig,
  TableSortConfig,
} from "shared/components/UniversalTable/types";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import {
  getPrimaryTicketSupplier,
  getTicketSupplierNames,
} from "../shared/ticketSuppliers";
import { helpdeskPhaseOrder } from "../helpdeskPhaseConfig";

// ========== COLUMN CONFIGURATION ==========

/**
 * Helpdesk ticket table column definitions
 *
 * De volgorde hier moet gelijk lopen met de cellen uit `createHelpdeskCells`:
 * UniversalTableRow rendert cellen op index tegen dit grid, niet op id.
 *
 * Defines all columns in the helpdesk table with their:
 * - Width behavior (fixed vs flexible)
 * - Sort keys
 * - Responsive visibility
 */
export const HELPDESK_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    {
      id: "checkbox",
      label: "", // Checkbox component will be inserted by table
      minWidth: 40,
      defaultWeight: 0, // Fixed width
      className: "flex justify-center",
    },
    {
      id: "actions",
      label: "Acties",
      minWidth: 120,
      defaultWeight: 0, // Fixed width for 4 action buttons (removed smart insights)
      className: "flex justify-center",
    },
    {
      id: "comm",
      label: "Comm",
      minWidth: 80,
      defaultWeight: 0, // Fixed width for 2 communication buttons
      className: "flex justify-center",
    },
    {
      id: "priority",
      label: "Prio",
      minWidth: 60,
      defaultWeight: 0, // Fixed width for 3 stars
      sortKey: "priority",
      className: "flex justify-center",
    },
    {
      id: "insurance",
      label: "Verz",
      minWidth: 50,
      defaultWeight: 0, // Fixed width for insurance button
      className: "flex justify-center",
    },
    {
      id: "ticket-label",
      label: "Ticket & Label",
      // Titel + labelchip + gebouwbadges staan in één cel; op 150px werd de titel al
      // na een paar woorden afgekapt en bleef er van de labelchip een letter over.
      // De ondergrens blijft bewust gematigd — het gewicht hieronder doet het werk,
      // een te hoge `minWidth` duwt de tabel op een smal scherm in de horizontale scroll.
      minWidth: 220,
      defaultWeight: 14, // Main content column, takes most space
      sortKey: "label",
    },
    {
      id: "building",
      label: "Gebouw",
      minWidth: 80,
      defaultWeight: 3, // Flexible
      sortKey: "building",
      visible: "lg", // Hidden on mobile/tablet
    },
    {
      id: "unit",
      label: "Unit",
      minWidth: 70,
      defaultWeight: 2, // Flexible
      sortKey: "unit",
      visible: "lg", // Hidden on mobile/tablet
    },
    {
      id: "source",
      label: "Bron",
      minWidth: 18,
      defaultWeight: 0, // Fixed width (fits single letter)
      sortKey: "source",
      visible: "xl", // Hidden on small/medium screens
    },
    {
      id: "reporter",
      label: "Melder",
      minWidth: 100,
      defaultWeight: 2.5, // Flexible
      sortKey: "reporter",
      visible: "lg", // Hidden on mobile/tablet
    },
    {
      id: "supplier",
      label: "Leverancier",
      minWidth: 100,
      defaultWeight: 2.5, // Flexible
      sortKey: "supplier",
      visible: "lg", // Hidden on mobile/tablet
    },
    {
      id: "assignee",
      label: "Toegewezen",
      minWidth: 80,
      defaultWeight: 1.5, // Flexible (reduced from 2)
      sortKey: "assignee",
    },
    {
      id: "internal-status",
      label: "Fase",
      // Ruimte vrijgemaakt voor "Ticket & Label". De fasenamen zijn lang
      // ("Bijkomende info noodzakelijk na dagelijkse helpdeskmeeting") en pasten ook op
      // 120px niet, dus kortte de cel ze zonder meer af. De pill kapt nu af met "…" en
      // de volledige fase staat in de tooltip (zie helpdeskCellFactory).
      minWidth: 88,
      defaultWeight: 1, // Flexible (reduced from 2)
      sortKey: "internalStatus",
      visible: "lg", // Hidden on mobile/tablet
    },
    {
      id: "days-in-phase",
      label: "Dagen in Fase",
      minWidth: 80,
      defaultWeight: 0, // Fixed width
      sortKey: "daysInCurrentPhase",
      visible: "lg", // Hidden on mobile/tablet
    },
    {
      id: "interventions",
      label: "Interventiedatum",
      minWidth: 78,
      defaultWeight: 0, // Fixed width — dd/mm/jj (+N)
      visible: "lg", // Hidden on mobile/tablet
      // `normal-case` overschrijft de hoofdletters die de tabelkop standaard op
      // elk label legt: als lange kop schreeuwt "INTERVENTIEDATUM" te hard.
      className: "normal-case",
      headerAlign: "center", // Kop boven de (gecentreerde) datum
    },
    {
      id: "invoice-total",
      label: "Factuur",
      minWidth: 72,
      defaultWeight: 0, // Fixed width — bedrag zonder centen
      sortKey: "actualCost",
      visible: "lg", // Hidden on mobile/tablet
      headerAlign: "center", // Kop boven het (gecentreerde) bedrag
    },
    {
      id: "date",
      label: "Datum",
      // Aanmaakdatum is de minst belangrijke kolom: kort formaat (dd/mm/jj), dus
      // net genoeg breedte daarvoor.
      minWidth: 52,
      defaultWeight: 0, // Fixed width
      sortKey: "date",
      visible: "lg", // Hidden on mobile/tablet
    },
  ],
  // v6: "Ticket & Label" breder ten koste van "Fase". De breedtes worden bij élke
  // mount weggeschreven, dus zonder nieuwe sleutel zou iedereen die de lijst al
  // geopend heeft de oude waarden blijven zien.
  localStorageKey: "helpdeskListColumnWidthsV6",
};

// ========== PICKER FIELD CONFIGURATION ==========

/**
 * Helpdesk picker field mappings
 *
 * Defines how cell clicks map to picker modals and how updates are processed.
 * Each mapping specifies:
 * - Which cell event triggers which picker
 * - How to extract current values from rows
 * - How to build API update payloads
 * - Custom update handlers for special cases
 */
export const HELPDESK_PICKER_CONFIG: PickerConfig = {
  entityName: "ticket",
  entityPrefix: "H",
  apiUpdateEndpoint: "/helpdesk/ticket/{id}",
  entityPlural: "tickets",

  fieldMappings: [
    // Label field
    {
      eventMode: "label",
      pickerMode: "helpdesk-label",
      fieldName: "label",
      titleTemplate: "Selecteer label voor {count} {entityName}",
      extractCurrentValue: (row) => row.label,
      buildUpdatePayload: (val) => ({ label: val }),
    },

    // Assignee field
    {
      eventMode: "assignee",
      pickerMode: "assignee",
      fieldName: "assignedTo",
      titleTemplate: "Selecteer verantwoordelijke voor {count} {entityName}",
      extractCurrentValue: (row) =>
        row.assignedTo ? { id: row.assignedTo } : null,
      buildUpdatePayload: (val) => ({ assignedTo: val?.id || null }),
    },

    // Building field
    {
      eventMode: "building",
      pickerMode: "building",
      fieldName: "building",
      titleTemplate: "Selecteer gebouw voor {count} {entityName}",
      extractCurrentValue: (row) => row.building,
      buildUpdatePayload: (val) => ({ building: val ? { id: val.id } : null }),
    },

    // Unit field
    {
      eventMode: "unit",
      pickerMode: "unit",
      fieldName: "unit",
      titleTemplate: "Selecteer unit voor {count} {entityName}",
      extractCurrentValue: (row) => row.unit,
      extractContextValue: (row) => row.building?.id, // Pass building ID for filtering
      buildUpdatePayload: (val) => ({ unit: val ? { id: val.id } : null }),
    },

    // Supplier field (special: multi-leverancier — bulk voegt een leverancier TOE)
    {
      eventMode: "supplier",
      pickerMode: "supplier",
      fieldName: "supplier",
      titleTemplate: "Leverancier toevoegen aan {count} {entityName}",
      extractCurrentValue: (row) => getPrimaryTicketSupplier(row),
      customUpdateHandler: async (val, ticketIds, session, onUpdate) => {
        // Multi-leverancier: een ticket kan meerdere leveranciers hebben.
        // Bulk-actie voegt de gekozen leverancier toe via het dedicated endpoint.
        if (!val?.id) return;
        for (const id of ticketIds) {
          try {
            const response = await fetchWithAuth(
              `${API_URL}/helpdesk/${id}/suppliers`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ supplierId: val.id }),
              },
              session,
            );
            if (!response.ok) {
              throw new Error(`Failed to add supplier for ticket ${id}`);
            }

            // Endpoint geeft de volledige (gesorteerde) assignment-lijst terug,
            // inclusief eager geladen supplier. Daarmee de lokale rij bijwerken.
            const updatedAssignments = await response.json().catch(() => null);
            if (Array.isArray(updatedAssignments)) {
              onUpdate(id, { supplierAssignments: updatedAssignments });
            }
          } catch (error) {
            console.error(`Failed to add supplier for ticket ${id}:`, error);
            throw error; // Re-throw to trigger error toast
          }
        }
      },
    },

    // Reporter field
    {
      eventMode: "reporter",
      pickerMode: "reporter",
      fieldName: "reporter",
      titleTemplate: "Selecteer melder voor {count} {entityName}",
      extractCurrentValue: (row) => row.reporter,
      buildUpdatePayload: (val) => ({ reporter: val ? { id: val.id } : null }),
    },

    // Source field
    {
      eventMode: "source",
      pickerMode: "source",
      fieldName: "source",
      titleTemplate: "Selecteer bron voor {count} {entityName}",
      extractCurrentValue: (row) => row.source,
      buildUpdatePayload: (val) => ({ source: val }),
    },

    // Internal Status (Fase) field
    {
      eventMode: "internal-status",
      pickerMode: "helpdesk-status",
      fieldName: "internalStatus",
      titleTemplate: "Wijzig fase voor {count} {entityName}",
      extractCurrentValue: (row) => row.internalStatus,
      buildUpdatePayload: (val) => ({ internalStatus: val }),
    },
  ],

  // Human-readable field labels for success messages
  fieldLabels: {
    label: "Label",
    assignedTo: "Toegewezen aan",
    building: "Gebouw",
    unit: "Unit",
    supplier: "Leverancier",
    reporter: "Melder",
    source: "Bron",
    internalStatus: "Fase",
  },
};

// ========== SORT CONFIGURATION ==========

/**
 * Helpdesk table sort configuration
 *
 * Defines how to sort by each sortable column.
 * Includes Dutch-specific priority and status sorting.
 */
export const HELPDESK_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: "date",
  defaultDirection: "desc", // Most recent first

  fields: [
    // Priority: Hoog > Gemiddeld > Laag
    {
      key: "priority",
      extractor: (row) => {
        const priorityOrder: Record<string, number> = {
          Hoog: 3,
          Gemiddeld: 2,
          Laag: 1,
          Unknown: 0,
        };
        return priorityOrder[row.priority] || 0;
      },
    },

    // Building: Sort by building ID
    {
      key: "building",
      extractor: (row) => row.building?.id || "",
    },

    // Unit: Sort by unit ID
    {
      key: "unit",
      extractor: (row) => row.unit?.id || "",
    },

    // Date: Sort by creation date
    {
      key: "date",
      extractor: (row) => new Date(row.createdAt || "").getTime(),
    },

    // Supplier: Sort by supplier name(s)
    {
      key: "supplier",
      extractor: (row) => getTicketSupplierNames(row) || "",
    },

    // Factuurbedrag: som van de gekoppelde OPEX-factuurregels
    {
      key: "actualCost",
      extractor: (row) => row.actualCost || 0,
    },

    // Reporter: Sort by full name
    {
      key: "reporter",
      extractor: (row) => {
        if (!row.reporter) return "";
        const firstName = row.reporter.firstName || "";
        const lastName = row.reporter.lastName || "";
        return `${firstName} ${lastName}`.trim().toLowerCase();
      },
    },

    // Assignee: Sort by full name
    {
      key: "assignee",
      extractor: (row) => {
        if (!row.assignedTo) return "";
        const firstName = row.assignedTo.first_name || "";
        const lastName = row.assignedTo.last_name || "";
        return `${firstName} ${lastName}`.trim().toLowerCase();
      },
    },

    // Source: Sort alphabetically
    {
      key: "source",
      extractor: (row) => row.source || "",
    },

    // Label: Sort alphabetically
    {
      key: "label",
      extractor: (row) => (row.label || "").toLowerCase(),
    },

    // Status: Sort by workflow order
    {
      key: "status",
      extractor: (row) => {
        const statusOrder: Record<string, number> = {
          Ontvangen: 5,
          Geanalyseerd: 4,
          Gepland: 3,
          Afronding: 2,
          Gesloten: 1,
          Unknown: 0,
        };
        return statusOrder[row.internalStatus] || 0;
      },
    },

    // Fase: sorteer op de volgorde van het bord.
    //
    // Deze tabel gebruikte een eigen lijst met labels die niet meer bestonden
    // ("Extra informatie vereist", "Leverancier moet gecontacteerd worden",
    // "Werken afgerond", …). Vijf van de negen fases kwamen er dus niet in voor
    // en kregen allemaal 0, waardoor sorteren op fase ze willekeurig door
    // elkaar zette. Nu uit `helpdeskPhaseOrder`, één bron.
    //
    // Aflopend genummerd zodat de vroegste fase bovenaan komt, zoals voorheen.
    {
      key: "internalStatus",
      extractor: (row) => {
        const index = helpdeskPhaseOrder.indexOf(row.internalStatus);
        // Onbekende of lege fase onderaan.
        return index === -1 ? 0 : helpdeskPhaseOrder.length - index;
      },
    },

    // Days in phase: Sort by number of days
    {
      key: "daysInCurrentPhase",
      extractor: (row) => row.daysInCurrentPhase || 0,
    },
  ],

  caseSensitive: false, // Case-insensitive string comparison
};

// ========== COMBINED CONFIGURATION ==========

/**
 * Complete helpdesk table configuration
 *
 * Convenience export that combines all configurations.
 * Use this to pass all configs at once to UniversalTable.
 *
 * @example
 * ```tsx
 * <UniversalTable
 *   {...HELPDESK_TABLE_CONFIG}
 *   rows={tickets}
 *   {...otherProps}
 * />
 * ```
 */
export const HELPDESK_TABLE_CONFIG = {
  columnsConfig: HELPDESK_COLUMNS_CONFIG,
  pickerConfig: HELPDESK_PICKER_CONFIG,
  sortConfig: HELPDESK_SORT_CONFIG,
};

/**
 * Sortering van de weergave **per gebouw**.
 *
 * Standaard op de volgorde die de backend meegaf (gebouw → unit → ticket-id).
 * Zonder die sleutel sorteert de tabel op ticketdatum en komen de gebouwgroepen
 * op hun nieuwste ticket te staan in plaats van op gebouwcode. Klikken op een
 * kolomkop blijft gewoon werken.
 */
export const HELPDESK_BUILDING_SORT_CONFIG: TableSortConfig = {
  ...HELPDESK_SORT_CONFIG,
  defaultSortKey: "groupOrder",
  defaultDirection: "asc",
  fields: [
    ...HELPDESK_SORT_CONFIG.fields,
    { key: "groupOrder", extractor: (row: any) => row.groupOrder ?? 0 },
  ],
};

/**
 * Tabelconfiguratie van de weergave per gebouw: dezelfde kolommen en dezelfde
 * pickers als de vlakke lijst, enkel een andere standaardsortering. Gebouw en
 * unit staan al in de accordeonkoppen, dus de kolommen hoeven niet te wijzigen.
 */
export const HELPDESK_BUILDING_TABLE_CONFIG = {
  columnsConfig: HELPDESK_COLUMNS_CONFIG,
  pickerConfig: HELPDESK_PICKER_CONFIG,
  sortConfig: HELPDESK_BUILDING_SORT_CONFIG,
};
